import { ref, computed } from "vue";
import { jsonClient } from "@/lib/raftApi.js";
import debounce from "lodash/debounce";
import raftUnits from "@/lib/raft_units";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import { usePersistedValues } from "@/composables/usePersistedValues";
import { useRoute } from "vue-router/composables";
import jsonApiFilterManager from "../lib/JsonApiFilterManager";

const { config } = useDrupalSettings();

// eslint-disable-next-line import/prefer-default-export
export function useChemicalContainers(contextNid, contextTimestamp = null) {
  const { route } = useRoute();
  const { setPersistedValue } = usePersistedValues({ route, module: "*" });

  const state = ref({
    emptyStateText: "There are currently no containers to list.",
    isBusy: false,
    response: null,
    pageOffset: 0,
    currentPage: 1,
    sortBy: "field_chemical_name_preferred",
    sortDesc: false,
    totalRecords: null,
    debouncedSetPersistedSort: debounce(
      (payload) => setPersistedValue(payload),
      300,
    ),
    fields: [
      {
        key: "field_chemical_name_preferred",
        label: "Chemical Name",
        class: "chemical-name-preferred",
        sortable: false,
      },
      {
        key: "field_chemical_type.field_chemdb_cas_number",
        label: "CAS Number",
        class: "chemdb-cas-number",
        sortable: false,
      },
      {
        key: "field_chemical_type.field_chemdb_physical_state",
        label: "State",
        formatter: "formatPhysicalState",
        sortable: false,
      },
      {
        key: "field_chemical_amount",
        label: "Amount",
        formatter: "formatAmount",
        sortable: false,
      },
      {
        key: "field_chemical_unit_of_measure",
        label: "Units",
        sortable: false,
      },
      {
        key: "og_audience[0].title",
        label: "Group",
        class: "chemical-group",
        sortable: false,
        hideOnGroup: "laboratory",
      },
      {
        key: "field_chemical_space.title",
        label: "Location",
        class: "chemical-space",
        sortable: false,
        hideOnGroup: "space",
      },
      {
        key: "field_chemical_container_id",
        label: "Container ID",
        class: "container-id",
        sortable: false,
      },
      { key: "chemical_container_edit", label: "Edit" },
      { key: "chemical_container_remove", label: "Remove" },
      { key: "field_chemical_bench_location", label: "Bench" },
      { key: "field_chemical_shelf_location", label: "Shelf" },
      {
        key: "field_chemical_room_location",
        label: "Specific Location Note",
      },
      {
        key: "changed",
        label: "Date Last Changed",
        formatter: "formatDateString",
        sortable: false,
        sortDirection: "desc",
      },
      {
        key: "field_chemical_date_last_confirm",
        label: "Date Last Confirmed",
        formatter: "formatDateString",
        sortable: false,
        sortDirection: "desc",
      },
      {
        key: "created",
        label: "Submission Date",
        formatter: "formatDateString",
        sortable: false,
        sortDirection: "desc",
      },
      {
        key: "field_chemical_received",
        label: "Date Received",
        formatter: "formatDateReceived",
        sortable: false,
        sortDirection: "desc",
      },
      {
        key: "field_chemical_expiration",
        label: "Expiration Date",
        formatter: "formatDateString",
        sortable: false,
      },
      {
        key: "field_chemical_manufacturer.name",
        label: "Manufacturer",
        sortable: false,
      },
      {
        key: "field_chemical_product_name",
        label: "Product Name",
        sortable: false,
      },
      {
        key: "field_chemical_product_number",
        label: "Product Number",
        sortable: false,
      },
      {
        key: "field_chemical_container_status",
        label: "Container Status",
        formatter: "formatContainerStatus",
        sortable: false,
      },
      {
        key: "field_chemical_container_po",
        label: "Purchase Order",
        sortable: false,
      },
      {
        key: "field_chemical_lot_number",
        label: "Lot Number",
        sortable: false,
      },
      {
        key: "field_chemical_container_owner.field_prof_display_name",
        label: "Chemical Owner",
        sortable: false,
      },
      {
        key: "field_chemical_note",
        label: "Notes",
        formatter: "formatNotes",
      },
    ],
  });

  const getGroupContextString = computed(() => {
    let groupContext = "";
    if (contextNid) {
      groupContext = "?rog-type=node&rog-id=" + contextNid;
    }
    return groupContext;
  });

  const table = ref(null);

  const tableResetScroll = () => {
    table.value?.$el?.scrollTo(0, 0);
  };

  const toggleBusy = () => {
    state.value.isBusy = !state.value.isBusy;
    tableResetScroll();
    table.value?.refresh();
  };

  const updateRole = () => {
    const newRole = document?.querySelector(
      ".b-table-empty-row > td:nth-child(1) > div:nth-child(1)",
    );
    if (newRole) {
      newRole.setAttribute("role", "status");
    }
  };

  const chemContainers = computed(() => {
    const containers = state.value.response
      ? state.value.response?.deserialized
      : [];
    if (containers?.length < 1) {
      tableResetScroll();
    }
    return containers;
  });

  const fieldsToInclude = [
    "field_chemical_type",
    "og_audience,field_chemical_space",
    "field_chemical_manufacturer",
    "og_audience.og_audience",
    "field_chemical_space.field_parent_location",
    "field_chemical_space.field_parent_location.field_parent_location",
    "field_chemical_container_owner",
  ];
  const fieldsForChemDbType = [
    "drupal_internal__nid",
    "title",
    "field_chemdb_cas_number",
    "field_chemdb_physical_state",
  ];
  const fieldsForChemContainer = [
    "drupal_internal__nid",
    "field_chemical_amount",
    "field_chemical_unit_of_measure",
    "field_chemical_type",
    "field_chemical_space",
    "og_audience",
    "field_chemical_manufacturer",
    "field_chemical_container_id",
    "field_chemical_container_status",
    "created",
    "changed",
    "field_chemical_container_type",
    "field_chemical_bench_location",
    "field_chemical_shelf_location",
    "field_chemical_room_location",
    "field_chemical_product_name",
    "field_chemical_product_number",
    "field_chemical_expiration",
    "field_chemical_received",
    "field_chemical_container_po",
    "field_chemical_note",
    "field_chemical_dea_controlled",
    "field_chemical_dea_finished_form",
    "field_chemical_dea_contain_size",
    "field_chemical_dea_dosage_units_",
    "field_chemical_name_preferred",
    "field_chemical_date_last_confirm",
    "operative_status",
    "field_chemical_container_owner",
    "field_chemical_lot_number",
  ];

  const updateInstitutionalChemicalContainers = async () => {
    const path = "/node/chemical_container";
    let queries = {};
    if (contextNid) {
      queries["rog-type"] = "node";
      queries["rog-id"] = contextNid;
      jsonApiFilterManager.addFilter(
        "recentContainerGroup",
        "og_audience.drupal_internal__nid",
        contextNid,
      );
    }
    queries = { ...queries, ...jsonApiFilterManager.queries };

    queries = {
      ...queries,
      sort: "-created",
      "fields[node--chemdb_type]": fieldsForChemDbType.join(),
      include: fieldsToInclude.join(),
      "fields[node--chemical_container": fieldsForChemContainer.join(),
    };

    if (contextTimestamp) {
      queries["filter[created][condition][path]"] = "created";
      queries["filter[created][condition][value]"] = contextTimestamp;
      queries["filter[created][condition][operator]"] = ">=";
    }

    return jsonClient
      .get({ path, queries })
      .catch((e) => {
        // eslint-disable-next-line
        console.error(e);
      })
      .then((response) => {
        if (response) {
          state.value.response = response;
          return Promise.resolve(state.value.response.deserialized);
        }
        return [];
      });
  };

  const updateInstitutionalChemicalContainersAsync = async () => {
    state.value.isBusy = true;
    try {
      await updateInstitutionalChemicalContainers();
      updateRole();
      toggleBusy();
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
      toggleBusy();
    }
  };

  const formatDateReceived = (value) => {
    // Handle the Date Received field differently from other dates, because the value is not in ISO 8601 format,
    // and the JS Date() object parses it differently, resulting in a date one day off.
    if (!value) {
      return;
    }
    let date = new Date(value);
    date =
      date.getUTCFullYear() +
      "-" +
      (date.getUTCMonth() + 1).toString().padStart(2, "0") +
      "-" +
      date.getUTCDate().toString().padStart(2, "0");
    return date;
  };

  const formatDateString = (value) => {
    if (!value) {
      return;
    }
    let date = new Date(value);
    date =
      date.getFullYear() +
      "-" +
      (date.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      date.getDate().toString().padStart(2, "0");
    return date;
  };

  const formatAmount = (value) => Number(value);

  const formatPhysicalState = (value) => {
    const states = raftUnits.physicalStates();
    return states[value];
  };

  const formatSdsOrigin = (item) => {
    const type = item?.field_chemical_sds?.type;
    if (!type) {
      return "";
    }
    let value;

    switch (type.replace(/raft_metadata--/, "")) {
      case "sds_external":
        value = "Centralized Database";
        break;
      case "sds_file":
        value = "Local Upload";
        break;
      case "sds_url":
        value = "URL";
        break;
      default:
        value = type;
    }
    if (!item?.field_chemical_sds?.field_current) {
      value = "Outdated (" + value + ")";
    }
    return value;
  };

  const buildContainerUrl = ({ container, suffix, edit }) => {
    let url = "";
    if (state.value.$isB4) {
      url = config.baseUrl + "/node/" + container.drupal_internal__nid;
    } else if (contextNid) {
      url =
        config.b3baseUrl +
        "/node/" +
        contextNid +
        "/chemtracker/container/" +
        container.id;
    } else {
      url = config.b3baseUrl + "/raft/chemtracker/container/" + container.id;
    }
    if (suffix) {
      url += suffix;
    }
    if (edit) {
      url += "/edit";
    }
    return url;
  };

  const buildChemicalTypeUrl = (container) => {
    const groupContext = state.value.getGroupContextString;

    if (state.value.$isB4) {
      let url =
        config.baseUrl +
        "/node/" +
        container.field_chemical_type.drupal_internal__nid;
      if (groupContext) {
        url += `/${groupContext}`;
      }
      return url;
    }

    let b3Url =
      config.b3baseUrl +
      "/raft/chemdb/chemical/" +
      container.field_chemical_type.id;
    if (groupContext) {
      b3Url += `/${groupContext}`;
    }
    return b3Url;
  };

  const buildNodeUrl = (nid) => "/node/" + nid;

  const buildUserUrl = (user) => "/user/" + user.drupal_internal__uid;

  const formatContainerStatus = (status) => {
    // @see \Drupal\chemical_container\ChemicalContainerHelper.
    switch (status) {
      case 0:
        return "Hidden";

      case 1:
        return "Normal";

      case 2:
        return "Surplus";
    }
  };

  const formatNotes = (notes) => {
    if (notes !== null && typeof notes === "object") {
      return notes.value;
    }
    return notes;
  };

  const formatters = {
    formatDateReceived,
    formatDateString,
    formatAmount,
    formatPhysicalState,
    formatSdsOrigin,
    formatContainerStatus,
    formatNotes,
  };

  const getFields = computed(() => {
    let fields = state.value.fields;
    if (fields) {
      if (state.value.getGroupType) {
        fields = fields.filter(
          (field) => field?.hideOnGroup !== state.value.getGroupType,
        );
      }
      const removeIndex = fields.findIndex(
        (f) => f?.key === "chemical_container_remove",
      );
      if (removeIndex > -1) {
        fields[removeIndex].label = state.value.getShowDeletedContainersFilter
          ? "Removed"
          : "Remove";
      }
      const dateIndex = fields.findIndex((f) => f?.key === "changed");
      if (dateIndex > -1) {
        fields[dateIndex].label = state.value.getShowDeletedContainersFilter
          ? "Date Removed"
          : "Date Last Changed";
      }
      if (state.value.getShowDeletedContainersFilter) {
        fields = fields.filter(
          (field) => field.key !== "chemical_container_edit",
        );
      }
      return fields.map((field) => ({
        ...field,
        formatter: formatters[field?.formatter],
      }));
    }
    return [];
  });

  const totalRecords = computed(() =>
    state.value.response ? state.value.response.meta?.count : 0,
  );

  const fetchContainerByUuid = async (uuid) => {
    const queries = {
      "fields[node--chemdb_type]": fieldsForChemDbType.join(),
      include: fieldsToInclude.join(),
      "fields[node--chemical_container": fieldsForChemContainer.join(),
    };
    return jsonClient.get({
      path: `/node/chemical_container/${uuid}`,
      queries,
    });
  };

  return {
    state,
    table,
    fetchContainerByUuid,
    getGroupContextString,
    getFields,
    chemContainers,
    updateInstitutionalChemicalContainersAsync,
    updateInstitutionalChemicalContainers,
    tableResetScroll,
    toggleBusy,
    updateRole,
    buildContainerUrl,
    buildChemicalTypeUrl,
    buildNodeUrl,
    buildUserUrl,
    formatters,
    totalRecords,
  };
}
